import Loader from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import {skipToken} from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import {memo, useCallback, useMemo} from 'react'

import {getProjectsAndOverviewReady} from '../../../../../../selectors/projectTrees'
import {restApi} from '../../../../../../services/rest'
import type {BuildTypeId, ChangeId, ProjectOrBuildTypeNode} from '../../../../../../types'
import {getProjectBuildTypeFilter, stringifyId} from '../../../../../../types'
import {ProjectBuildTypeSelect} from '../../../../../common/ProjectBuildTypeSelect/ProjectBuildTypeSelect'
import {useSequenceLoaderReadyHandler} from '../../../../../common/SequenceLoader/SequenceLoader.hooks'
import type {SeqProps} from '../../../../../common/SequenceLoader/SequenceLoader.types'
import {getChange} from '../../../Changes.selectors'

import {hierarchyToBuildTypesList} from './ChangeBuildTypeSelect.selectors'

import styles from './ChangeBuildTypeSelect.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = SeqProps & {
  className?: string
  changeId: ChangeId | null | undefined
  buildTypeId: BuildTypeId | null | undefined
  onChangeBuildTypeId: (buildTypeId: BuildTypeId | null | undefined) => void
}

function ChangeBuildTypeSelect({className, buildTypeId, changeId, onChangeBuildTypeId}: Props) {
  const personal = useAppSelector(state => getChange(state, changeId)?.personal ?? false)

  const activeItem = useMemo(() => getProjectBuildTypeFilter({buildTypeId}), [buildTypeId])

  const readyProjectsAndOverview = useAppSelector(state => getProjectsAndOverviewReady(state))

  const {ready, hierarchy} = restApi.endpoints.getChangeBuildTypeHierarchy.useQuery(
    changeId != null ? {modId: changeId, personal} : skipToken,
    {selectFromResult: ({data}) => ({ready: data != null, hierarchy: data})},
  )
  useSequenceLoaderReadyHandler(ready)
  const changeBuildTypeList = useMemo(() => hierarchyToBuildTypesList(hierarchy), [hierarchy])

  const onSelect = useCallback(
    (item: ProjectOrBuildTypeNode | null | undefined) => {
      switch (item?.nodeType) {
        case 'bt':
          onChangeBuildTypeId(stringifyId(item.id))
          break

        default:
      }
    },
    [onChangeBuildTypeId],
  )
  return (
    <div className={classNames(className, styles.wrapper)}>
      {ready && readyProjectsAndOverview ? (
        <ProjectBuildTypeSelect
          className={styles.select}
          popupClassName={styles.selectPopup}
          selected={activeItem}
          onSelect={onSelect}
          expandAll
          includedBuildTypes={changeBuildTypeList}
        />
      ) : (
        <Loader>{'Loading...'}</Loader>
      )}
    </div>
  )
}

export default memo(ChangeBuildTypeSelect)
